import { SearchOutlined } from '@ant-design/icons';
import { Col, Input, Row, Space, TreeSelect, Tooltip } from 'antd';
import ClearFilter from 'components/Global/ClearFilter';
// import ExportData from 'components/Global/ExportData';
import PayPeriodFilter from 'components/Global/PayPeriodFilter';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createModifiedArray, getOptionLabel } from 'utils/utils';

interface Props {
	handlePayPeriod: (payPeriodId: string) => void;
	selectedPayPeriod: string | null;
	performFilterHandler: (key: string, value: any) => void;
	performSearchHandler: (value: string) => void;
	searchValue: string;
	filterValue: any;
	clearFilter: () => void;
}

const SearchAndFilter = (props: Props) => {
	const {
		selectedPayPeriod,
		handlePayPeriod,
		clearFilter,
		performFilterHandler,
		performSearchHandler,
		filterValue,
		searchValue,
	} = props;

	const [filteredClassList, setFilteredClassList] = useState([]);

	const { data: configurations } = useSelector(
		(state: any) => state?.configuration
	);

	// const { payRollSummaryData } = useSelector((state: any) => state.reports);
	const { optionData: customer } = useSelector((state: any) => state.customer);
	const { optionData: classList } = useSelector((state: any) => state.class);
	const { data: employees } = useSelector((state: any) => state.employees);

	// Local state for immediate UI updates
	const [localEmployeeIds, setLocalEmployeeIds] = useState<string[]>(
		(filterValue?.employeeId as string[]) || []
	);
	const [localCustomerIds, setLocalCustomerIds] = useState<string[]>(
		(filterValue?.customerId as string[]) || []
	);
	const [localClassIds, setLocalClassIds] = useState<string[]>(
		(filterValue?.classId as string[]) || []
	);
	const [employeesTooltipDisabled, setEmployeesTooltipDisabled] = useState(false);
	const [customersTooltipDisabled, setCustomersTooltipDisabled] = useState(false);
	const [classesTooltipDisabled, setClassesTooltipDisabled] = useState(false);

	// Debounce refs for filters
	const employeeDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const customerDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const classDebounceRef = useRef<NodeJS.Timeout | null>(null);

	// Sync local state with filterValue when it changes externally (e.g., clear filter)
	useEffect(() => {
		setLocalEmployeeIds((filterValue?.employeeId as string[]) || []);
	}, [filterValue?.employeeId]);

	useEffect(() => {
		setLocalCustomerIds((filterValue?.customerId as string[]) || []);
	}, [filterValue?.customerId]);

	useEffect(() => {
		setLocalClassIds((filterValue?.classId as string[]) || []);
	}, [filterValue?.classId]);

	// Debounced handlers for filters
	const debouncedSetEmployees = useCallback(
		(employeeIds: string[]) => {
			setLocalEmployeeIds(employeeIds);
			if (employeeDebounceRef.current) {
				clearTimeout(employeeDebounceRef.current);
			}
			employeeDebounceRef.current = setTimeout(() => {
				performFilterHandler('employeeId', employeeIds);
			}, 500);
		},
		[performFilterHandler]
	);

	const debouncedSetCustomers = useCallback(
		(customerIds: string[]) => {
			setLocalCustomerIds(customerIds);
			if (customerDebounceRef.current) {
				clearTimeout(customerDebounceRef.current);
			}
			customerDebounceRef.current = setTimeout(() => {
				performFilterHandler('customerId', customerIds);
			}, 500);
		},
		[performFilterHandler]
	);

	const debouncedSetClasses = useCallback(
		(classIds: string[]) => {
			setLocalClassIds(classIds);
			if (classDebounceRef.current) {
				clearTimeout(classDebounceRef.current);
			}
			classDebounceRef.current = setTimeout(() => {
				performFilterHandler('classId', classIds);
			}, 500);
		},
		[performFilterHandler]
	);

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (employeeDebounceRef.current) {
				clearTimeout(employeeDebounceRef.current);
			}
			if (customerDebounceRef.current) {
				clearTimeout(customerDebounceRef.current);
			}
			if (classDebounceRef.current) {
				clearTimeout(classDebounceRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (
			configurations &&
			configurations.settings &&
			classList &&
			classList.length
		) {
			const _classList: any = createModifiedArray(
				classList,
				configurations.settings[0].fields['f1'].value
			);
			setFilteredClassList(_classList);
		}
	}, [configurations, classList, selectedPayPeriod]);

	// Filter customer data to remove "Select Customer" option
	const filteredCustomerData = useMemo(() => {
		if (!Array.isArray(customer)) {
			return [];
		}
		return customer.filter((item: any) => {
			return item?.title !== 'Select Customer';
		});
	}, [customer]);

	// Filter class data to remove "Select Class" option
	const filteredClassData = useMemo(() => {
		if (!Array.isArray(filteredClassList)) {
			return [];
		}
		return filteredClassList.filter((item: any) => {
			return item?.title !== 'Select Class';
		});
	}, [filteredClassList]);

	// Transform employees to treeData format for TreeSelect
	const employeeTreeData = useMemo(() => {
		return employees?.map((employee: any) => ({
			title: employee.fullName,
			value: employee.id,
			key: employee.id,
			children: [],
		})) || [];
	}, [employees]);

	return (
		<div className={styles['search-filter']}>
			<div className={`${styles['search-filter__wrapper']}`}>
				<Row justify="space-between" align="middle" gutter={[16, 16]}>
					<Col>
						<Space wrap>
							<PayPeriodFilter
								payPeriodId={selectedPayPeriod}
								onChangePayPeriodId={(value) => handlePayPeriod(value)}
							/>

							<Input
								className={styles['search-filter__tree-filter']}
								placeholder="Search here..."
								style={{ fontSize: '12px', minHeight: '40px' }}
								suffix={<SearchOutlined />}
								onChange={(e) => performSearchHandler(e.target.value)}
								value={searchValue}
								size="large"
							/>

							<TreeSelect
								placeholder="Employees"
								value={localEmployeeIds}
								onChange={debouncedSetEmployees}
								showSearch
								allowClear
								multiple
								maxTagCount={0}
								size="large"
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								treeData={employeeTreeData}
								treeLine
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setEmployeesTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedEmployeeIds = localEmployeeIds;
									if (selectedEmployeeIds.length === 0) return 'Employees';
									const employeeLabels = selectedEmployeeIds.map((id: string) => {
										const employee = employees?.find((emp: any) => emp.id === id);
										return employee?.fullName || id;
									});
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{employeeLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== employeeLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={employeesTooltipDisabled ? false : undefined}
											trigger={employeesTooltipDisabled ? [] : ['hover']}
										>
											Employees ({selectedEmployeeIds.length})
										</Tooltip>
									);
								}}
							/>

							<TreeSelect
								placeholder="Customers"
								value={localCustomerIds}
								onChange={debouncedSetCustomers}
								size="large"
								allowClear
								multiple
								maxTagCount={0}
								treeDefaultExpandAll
								showSearch
								treeData={filteredCustomerData}
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								treeLine
								onDropdownVisibleChange={(open) => {
									setCustomersTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedCustomerIds = localCustomerIds;
									if (selectedCustomerIds.length === 0) return 'Customers';
									const customerLabels = selectedCustomerIds.map((id: string) => {
										return getOptionLabel(id, filteredCustomerData) || id;
									});
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{customerLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== customerLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={customersTooltipDisabled ? false : undefined}
											trigger={customersTooltipDisabled ? [] : ['hover']}
										>
											Customers ({selectedCustomerIds.length})
										</Tooltip>
									);
								}}
							/>

							<TreeSelect
								placeholder="Classes"
								value={localClassIds}
								onChange={debouncedSetClasses}
								size="large"
								allowClear
								multiple
								maxTagCount={0}
								treeDefaultExpandAll
								showSearch
								treeData={filteredClassData}
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								filterTreeNode={(input, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
								treeLine
								onDropdownVisibleChange={(open) => {
									setClassesTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedClassIds = localClassIds;
									if (selectedClassIds.length === 0) return 'Classes';
									const classLabels = selectedClassIds.map((id: string) => {
										return getOptionLabel(id, filteredClassData) || id;
									});
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{classLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== classLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={classesTooltipDisabled ? false : undefined}
											trigger={classesTooltipDisabled ? [] : ['hover']}
										>
											Classes ({selectedClassIds.length})
										</Tooltip>
									);
								}}
							/>

							<div className={styles['scaled-clear-filter']}>
								<ClearFilter clearFilter={clearFilter} />
							</div>
						</Space>
					</Col>

					{/* Optional right-side action buttons (currently commented out) */}
					{/* {payRollSummaryData?.length > 0 && (
		<Col>
			<Space>
				<ExportData
					moduleName="payrollSummaryReport"
					params={{ payPeriodId: selectedPayPeriod, filterValue, searchValue }}
				/>
			</Space>
		</Col>
	)} */}
				</Row>

				{/* <Row justify="space-between">
					<Col>
						<Space>
							<PayPeriodFilter
								payPeriodId={selectedPayPeriod}
								onChangePayPeriodId={(value) => {
									handlePayPeriod(value);
								}}
							/>
							<Input
								className={styles['search-filter__tree-filter']}
								placeholder="Search here..."
								style={{ fontSize: '12px', minHeight: '40px' }}
								suffix={<SearchOutlined />}
								onChange={(e) => {
									performSearchHandler(e.target.value);
								}}
								value={searchValue}
								size="large"
							/>

							<Select
								className={styles['search-filter__filter']}
								style={{ width: 200 }}
								onChange={(value) => performFilterHandler('employeeId', value)}
								size="large"
								placeholder="Employees"
								value={filterValue?.employeeId}
								showSearch
								filterOption={(input, option: any) =>
									(option?.children as string)
										.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0
								}
							>
								<Select.Option value="">Select Employee</Select.Option>
								{employees?.map((employee: any, index: number) => {
									return (
										<Select.Option value={employee?.id} key={index}>
											{employee?.fullName}
										</Select.Option>
									);
								})}
							</Select>

							<TreeSelect
								className={styles['search-filter__tree-filter']}
								dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
								size="large"
								placeholder="Customer"
								value={filterValue?.customerId}
								showSearch
								onChange={(value) => performFilterHandler('customerId', value)}
								treeData={customer}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) => {
									// Customize the filtering logic here
									return treeNode.props.title
										.toLowerCase()
										.includes(inputValue.toLowerCase());
								}}
							/>
							<TreeSelect
								showSearch
								className={styles['search-filter__tree-filter']}
								style={{ width: 200 }}
								onChange={(value) => {
									performFilterHandler('classId', value);
								}}
								value={filterValue?.classId}
								placeholder="Class"
								allowClear={false}
								treeDefaultExpandAll
								size="large"
								treeData={filteredClassList}
								filterTreeNode={(inputValue, treeNode) => {
									// Customize the filtering logic here
									return treeNode.props.title
										.toLowerCase()
										.includes(inputValue.toLowerCase());
								}}
							/>

	<div className={styles['scaled-clear-filter']}>
										<ClearFilter clearFilter={clearFilter} />
									</div>						</Space>
					</Col>
					{/* {payRollSummaryData && payRollSummaryData.length > 0 && (
						<Col>
							<Space className="search-filter-export">
								<ExportData
									moduleName="payrollSummaryReport"
									params={{
										payPeriodId: selectedPayPeriod,
										filterValue,
										searchValue,
									}}
								/>
							</Space>
						</Col>
					)} */}
				{/* </Row> */}
			</div>
		</div>
	);
};

export default SearchAndFilter;
